import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Table } from 'reactstrap';

import customerApi, { IContactBranch } from 'api/contacts';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';

const FIELDS: (keyof Omit<IContactBranch, 'id'>)[] = [
  'name',
  'operatingArea',
  'streetAddress',
  'postalNumber',
  'city',
];

const emptyBranch = (country: string): Omit<IContactBranch, 'id'> => ({
  city: '',
  country,
  name: '',
  operatingArea: '',
  postalNumber: '',
  streetAddress: '',
});

/**
 * The departments or areas of a company (osastot / alueet): the same business id, but a
 * regional visiting address and regional contact persons. Each row is saved on its own; a
 * removed one leaves its contact persons common to all the areas.
 */
const ContactBranches = ({
  addMessage,
  canWrite,
  contactId,
  country,
}: {
  addMessage: (message: string) => void;
  canWrite: boolean;
  contactId: string;
  // The country of the company, the default of a new one
  country: string;
}): JSX.Element => {
  const { t } = useTranslation([translations.contacts]);
  const text = (key: string) => t(`${translations.contacts}:branches.${key}`);

  const [branches, setBranches] = useState<IContactBranch[]>([]);
  // The rows changed and not yet saved
  const [drafts, setDrafts] = useState<Record<number, IContactBranch>>({});
  const [newBranch, setNewBranch] = useState(emptyBranch(country));
  const [saving, setSaving] = useState(false);

  const load = () =>
    customerApi.branches
      .get(contactId)
      .then(setBranches)
      .catch(() => undefined);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const run = async (action: () => Promise<unknown>, message: string) => {
    setSaving(true);
    try {
      await action();
      await load();
      addMessage(text(message));
      return true;
    } catch (error) {
      addMessage(text('Save failed'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async (branch: IContactBranch) => {
    if (branch.name.trim() === '') {
      addMessage(text('Name required'));
      return;
    }
    if (await run(() => customerApi.branches.put(branch), 'Saved')) {
      setDrafts((current) => {
        const rest = { ...current };
        delete rest[branch.id];
        return rest;
      });
    }
  };

  const add = async () => {
    if (newBranch.name.trim() === '') {
      addMessage(text('Name required'));
      return;
    }
    if (
      await run(() => customerApi.branches.post(contactId, newBranch), 'Added')
    ) {
      setNewBranch(emptyBranch(country));
    }
  };

  const remove = (branch: IContactBranch) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(text('Remove confirmation'))) {
      run(() => customerApi.branches.remove(branch.id), 'Removed');
    }
  };

  return (
    <div className="contact-branches">
      <h3>{text('Title')}</h3>
      <p className="contact-branches-info">{text('Info')}</p>
      <Table size="sm" className="contact-branches-table">
        <thead>
          <tr>
            {FIELDS.map((field) => (
              <th key={field}>{text(field)}</th>
            ))}
            {canWrite ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {branches.map((saved) => {
            const branch = drafts[saved.id] ?? saved;
            const changed = drafts[saved.id] !== undefined;
            return (
              <tr key={saved.id}>
                {FIELDS.map((field) => (
                  <td key={field}>
                    <Input
                      aria-label={text(field)}
                      bsSize="sm"
                      disabled={!canWrite}
                      onChange={(event) => {
                        // Read before the event is reused (React 16)
                        const { value } = event.target;
                        setDrafts((current) => ({
                          ...current,
                          [saved.id]: { ...branch, [field]: value },
                        }));
                      }}
                      value={branch[field] ?? ''}
                    />
                  </td>
                ))}
                {canWrite ? (
                  <td className="contact-branches-actions">
                    <PrimaryButton
                      disabled={saving || !changed}
                      onClick={() => save(branch)}
                    >
                      {t(`${translations.common}:Save`)}
                    </PrimaryButton>
                    <button
                      className="contact-branches-remove"
                      disabled={saving}
                      onClick={() => remove(saved)}
                      title={text('Remove')}
                      type="button"
                    >
                      ✕
                    </button>
                  </td>
                ) : null}
              </tr>
            );
          })}
          {canWrite ? (
            <tr className="contact-branches-new">
              {FIELDS.map((field) => (
                <td key={field}>
                  <Input
                    aria-label={text(field)}
                    bsSize="sm"
                    onChange={(event) =>
                      setNewBranch({
                        ...newBranch,
                        [field]: event.target.value,
                      })
                    }
                    placeholder={text(field)}
                    value={newBranch[field]}
                  />
                </td>
              ))}
              <td>
                <PrimaryButton disabled={saving} onClick={add}>
                  {`+ ${text('Add')}`}
                </PrimaryButton>
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </div>
  );
};

export default ContactBranches;
